import React, { useState, useRef, useEffect } from 'react';
import useStore from '../../store/useStore';
import { supabase } from '../../supabase';
import * as db from '../../db';
import { Toast, useToast, TOAST_ANIM } from '../ui';
import { Building2 } from 'lucide-react';
import GlobalSearch from './GlobalSearch';
import useCampuses from '../../hooks/useCampuses';
import { switchAndReload, takeSwitchedCampusName } from '../../services/campusSwitch';
import { faqsFor } from '../../utils/helpFaqs';
import { todayISO, localISO } from '../../utils/attendanceDate';

import { formatDayMonth } from '../../utils/formatDate';
// The topbar takes every colour from the theme variables that Settings -> Display & Theme
// sets (see applyTheme in utils/helpers). Nothing in here is specific to one theme.
const THEME_COLOR = 'var(--primary)';
const THEME_BORDER = 'var(--primary-200)';
const THEME_GRADIENT = 'linear-gradient(135deg, var(--primary-50) 0%, color-mix(in srgb, var(--primary-50) 55%, #ffffff) 100%)';
// Replaces the old "<color>0a" hex-alpha trick: pass the two hex digits, get --primary at that opacity
const tint = (hex) => `color-mix(in srgb, var(--primary) ${Math.round((parseInt(hex, 16) / 255) * 100)}%, transparent)`;

const PAGE_TITLES = {dashboard:'Dashboard',students:'Students',families:'Families',attendance:'Attendance',fees:'Fees',pos:'POS',inventory:'Inventory',exams:'Exams',staff:'Staff',reports:'Reports',settings:'Settings',platform:'Platform overview','platform-schools':'Schools','platform-payments':'Payments','platform-onboard':'Schools','platform-demo':'Demo access','platform-leads':'Leads'};
const PAGE_SUBS   = {dashboard:'Overview',students:'Manage records',families:'Parent & family info',attendance:'Daily marking',fees:'Collection & challans',pos:'Point of sale',inventory:'Stock & vendors',exams:'Results & marks',staff:'Staff & payroll',reports:'Analytics & exports',settings:'Configuration',platform:'All schools on EdEase','platform-schools':'Plans, modules and subscriptions','platform-payments':'Payments and invoices','platform-onboard':'Onboard a school','platform-demo':'Time-limited demo logins','platform-leads':'Sales leads and follow-ups'};

// Category -> pill colour + label, for both school_events.category and a synthetic
// 'holiday' category used for rows merged in from the holidays table.
const TAG_COLORS = {
  general:{bg:'#EAF3DE',color:'#3B6D11'}, exam:{bg:'#E6F1FB',color:'#185FA5'},
  ptm:{bg:'#EEEDFE',color:'#3C3489'}, sports:{bg:'#FAEEDA',color:'#854F0B'},
  trip:{bg:'#FCEBEB',color:'#A32D2D'}, celebration:{bg:'#FBEAF0',color:'#993556'},
  holiday:{bg:'#FAEEDA',color:'#854F0B'},
};
const CATEGORY_LABEL = {general:'General',exam:'Exam',ptm:'PTM',sports:'Sports',trip:'Trip',celebration:'Celebration',holiday:'Holiday'};

// Messages has no backend (no messaging table/component anywhere) — not offered as a widget.
const ALL_WIDGETS = [
  {id:'events',        label:'Events',        dot:null, type:'system'},
  {id:'notifications', label:'Notifications', dot:null, type:'system'},
  {id:'help',          label:'Help',          dot:null, type:'system'},
];

// Quick-action shortcut buttons — user toggles from profile
const QUICK_ACTIONS = [
  {id:'qa_fees',      label:'Fees',       page:'fees',       color:'#6366f1'},
  {id:'qa_admission', label:'Admission',  page:'students',   color:'#16a34a'},
  {id:'qa_reports',   label:'Reports',    page:'reports',    color:'#0284c7'},
  {id:'qa_pos',       label:'POS',        page:'pos',        color:'#7c3aed'},
  {id:'qa_attendance',label:'Attendance', page:'attendance', color:'#b45309'},
  {id:'qa_inventory', label:'Inventory',  page:'inventory',  color:'#0f766e'},
  {id:'qa_exams',     label:'Exams',      page:'exams',      color:'#dc2626'},
  {id:'qa_staff',     label:'Staff',      page:'staff',      color:'#1d4ed8'},
];

// ─── SVG ICONS ───────────────────────────────────────────
const IC = {
  calendar:   <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  bell:       <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  message:    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  help:       <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  search:     <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  userPlus:   <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>,
  chevDown:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  chevRight:  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  pin:        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  user:       <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  settings:   <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  logout:     <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  edit:       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  grip:       <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/></svg>,
  x:          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  receipt:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  chart:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  cart:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
  clipboard:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>,
  package:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  writing:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
  badge:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
};
const WIC = {events:IC.calendar,notifications:IC.bell,help:IC.help};
const QA_ICONS = {qa_fees:IC.receipt,qa_admission:IC.userPlus,qa_reports:IC.chart,qa_pos:IC.cart,qa_attendance:IC.clipboard,qa_inventory:IC.package,qa_exams:IC.writing,qa_staff:IC.badge};

function loadPrefs(){try{const s=JSON.parse(localStorage.getItem('topbarWidgets'));if(s?.order)return s;}catch{}return{order:ALL_WIDGETS.map(w=>w.id),hidden:[],qaVisible:[]};}
function savePrefs(p){try{localStorage.setItem('topbarWidgets',JSON.stringify(p));}catch{}}

// ─── EVENTS PANEL ────────────────────────────────────────
// events: real rows from school_events + holidays (next 30 days), merged and sorted by date —
// built in Topbar()'s useUpcomingEvents effect below. No mock data.
function EventsPanel({border,events,loading}){
  return(
    <div data-menu="calendar" style={{position:'absolute',top:'calc(100% + 8px)',right:0,zIndex:300,background:'#fff',border:`1.5px solid ${border}`,borderRadius:14,width:310,boxShadow:'0 8px 24px rgba(99,102,241,.12)',overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 14px',borderBottom:`1px solid ${border}`,background:`${tint('0a')}`}}>
        <span style={{fontSize:13,fontWeight:700,color:'var(--text-primary)'}}>Upcoming Events</span>
        <span style={{fontSize:10,background:'#FAEEDA',color:'#854F0B',padding:'2px 7px',borderRadius:10,fontWeight:600}}>{events.length} events</span>
      </div>
      <div style={{padding:'3px 14px 5px',borderBottom:`1px solid ${border}`,background:'#fafbff'}}><span style={{fontSize:10,color:'var(--text-hint)'}}>From the school calendar · next 30 days</span></div>
      {loading && <div style={{padding:'20px 14px',textAlign:'center',color:'var(--text-hint)',fontSize:12}}>Loading…</div>}
      {!loading && events.length===0 && <div style={{padding:'20px 14px',textAlign:'center',color:'var(--text-hint)',fontSize:12}}>No events in the next 30 days.</div>}
      {!loading && events.map(ev=>{const d=new Date(ev.date);const tc=TAG_COLORS[ev.category]||TAG_COLORS.general;return(
        <div key={ev.id} style={{display:'flex',gap:10,padding:'9px 14px',borderBottom:'1px solid #f8fafc',cursor:'pointer'}} onMouseOver={e=>e.currentTarget.style.background='#fafbff'} onMouseOut={e=>e.currentTarget.style.background=''}>
          <div style={{textAlign:'center',flexShrink:0,width:28}}><div style={{fontSize:17,fontWeight:700,color:'var(--text-primary)',lineHeight:1}}>{d.getDate()}</div><div style={{fontSize:9,color:'var(--text-hint)',textTransform:'uppercase',letterSpacing:'.5px'}}>{d.toLocaleString('en',{month:'short'})}</div></div>
          <div style={{flex:1,minWidth:0}}><div style={{fontSize:12,fontWeight:600,color:'var(--text-primary)'}}>{ev.title}<span style={{fontSize:9,background:tc.bg,color:tc.color,padding:'1px 6px',borderRadius:8,marginLeft:5,fontWeight:600}}>{CATEGORY_LABEL[ev.category]||ev.category}</span></div>{ev.subtitle&&<div style={{fontSize:11,color:'var(--text-hint)',marginTop:2,display:'flex',alignItems:'center',gap:3}}>{IC.pin}{ev.subtitle}</div>}</div>
        </div>
      );})}
    </div>
  );
}

// ─── NOTIFICATIONS PANEL ─────────────────────────────────
// items: real, computed signals — built in Topbar()'s useRoleNotifications effect below, each
// one only included if the signed-in user holds the matching page permission. No mock data.
function NotifPanel({border,items,loading}){
  return(
    <div data-menu="notif" style={{position:'absolute',top:'calc(100% + 8px)',right:0,zIndex:300,background:'#fff',border:`1.5px solid ${border}`,borderRadius:14,width:295,boxShadow:'0 8px 24px rgba(99,102,241,.12)',overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 14px',borderBottom:`1px solid ${border}`,background:`${tint('0a')}`}}>
        <span style={{fontSize:13,fontWeight:700,color:'var(--text-primary)'}}>Notifications</span>
        {items.length>0&&<span style={{fontSize:10,background:'#FCEBEB',color:'#A32D2D',padding:'2px 7px',borderRadius:10,fontWeight:600}}>{items.length} new</span>}
      </div>
      {loading && <div style={{padding:'20px 14px',textAlign:'center',color:'var(--text-hint)',fontSize:12}}>Loading…</div>}
      {!loading && items.length===0 && <div style={{padding:'20px 14px',textAlign:'center',color:'var(--text-hint)',fontSize:12}}>Nothing needs your attention right now.</div>}
      {!loading && items.map((n,i)=>(
        <div key={i} onClick={n.onClick} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 14px',borderBottom:'1px solid #f8fafc',cursor:n.onClick?'pointer':'default'}} onMouseOver={e=>e.currentTarget.style.background='#fafbff'} onMouseOut={e=>e.currentTarget.style.background=''}>
          <div style={{width:30,height:30,borderRadius:8,background:n.bg,display:'flex',alignItems:'center',justifyContent:'center',color:n.c,flexShrink:0}}>{IC.bell}</div>
          <div style={{flex:1}}><div style={{fontSize:12,fontWeight:600,color:'var(--text-primary)',lineHeight:1.4}}>{n.text}</div><div style={{fontSize:11,color:'var(--text-hint)',marginTop:2}}>{n.sub}</div></div>
          <div style={{width:7,height:7,borderRadius:'50%',background:n.dc||'#E24B4A',flexShrink:0,marginTop:4}}/>
        </div>
      ))}
    </div>
  );
}

// ─── HELP / FAQ PANEL ────────────────────────────────────
// Only the topics this user's role can actually use (see utils/helpFaqs.js).
function HelpPanel({border,faqs,onClose}){
  const [open,setOpen]=useState(null);
  const [search,setSearch]=useState('');
  const needle=search.trim().toLowerCase();
  const filtered=faqs.filter(f=>!needle||f.q.toLowerCase().includes(needle)||f.topic.toLowerCase().includes(needle)||f.steps.join(' ').toLowerCase().includes(needle));
  let lastTopic=null;
  return(
    <div data-menu="help" style={{position:'absolute',top:'calc(100% + 8px)',right:0,zIndex:300,background:'#fff',border:`1.5px solid ${border}`,borderRadius:14,width:400,maxHeight:'80vh',boxShadow:'0 8px 24px rgba(99,102,241,.12)',overflow:'hidden',display:'flex',flexDirection:'column'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 14px',borderBottom:`1px solid ${border}`,background:`${tint('0a')}`,flexShrink:0}}>
        <div><div style={{fontSize:13,fontWeight:700,color:'var(--text-primary)'}}>Help</div><div style={{fontSize:10,color:'var(--text-hint)',marginTop:1}}>{faqs.length} topics for your role</div></div>
        <button type="button" aria-label="Close help" onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-hint)',display:'flex',alignItems:'center'}}>{IC.x}</button>
      </div>
      <div style={{padding:'8px 14px',borderBottom:`1px solid ${border}`,flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:7,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:8,padding:'6px 10px'}}>
          {IC.search}
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search help" aria-label="Search help" style={{border:'none',outline:'none',fontSize:12,color:'var(--text-primary)',background:'transparent',width:'100%'}}/>
          {search&&<button type="button" aria-label="Clear search" onClick={()=>setSearch('')} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-hint)',fontSize:16,lineHeight:1,padding:0}}>×</button>}
        </div>
      </div>
      <div style={{overflowY:'auto',flex:1}}>
        {filtered.length===0&&<div style={{padding:'24px 14px',textAlign:'center',color:'var(--text-hint)',fontSize:13}}>{faqs.length===0?'No help topics for your role yet.':`Nothing matches "${search}"`}</div>}
        {filtered.map(f=>{
          const header=f.topic!==lastTopic;
          lastTopic=f.topic;
          const isOpen=open===f.q;
          return(
            <div key={f.q}>
              {header&&<div style={{padding:'9px 14px 4px',fontSize:10,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--text-hint)'}}>{f.topic}</div>}
              <div style={{borderBottom:'1px solid #f8fafc'}}>
                <button type="button" aria-expanded={isOpen} onClick={()=>setOpen(isOpen?null:f.q)} style={{display:'flex',alignItems:'center',justifyContent:'space-between',width:'100%',padding:'10px 14px',cursor:'pointer',gap:10,border:'none',background:'transparent',textAlign:'left'}}>
                  <span style={{fontSize:12,fontWeight:600,color:'var(--text-primary)',lineHeight:1.4,flex:1}}>{f.q}</span>
                  <span style={{flexShrink:0,color:'var(--text-hint)',transform:isOpen?'rotate(90deg)':'none',transition:'transform .2s'}}>{IC.chevRight}</span>
                </button>
                {isOpen&&(
                  <ol style={{margin:0,padding:'2px 14px 12px 32px',fontSize:12,color:'var(--text-secondary)',lineHeight:1.6,background:'#fafbff',borderTop:'1px solid #f1f3f9'}}>
                    {f.steps.map((st,i)=><li key={i} style={{marginTop:6}}>{st}</li>)}
                  </ol>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{padding:'8px 14px',borderTop:`1px solid ${border}`,background:'#fafbff',flexShrink:0,fontSize:11,color:'var(--text-hint)',textAlign:'center'}}>
        EdEase School ERP
      </div>
    </div>
  );
}

// ─── USER DROPDOWN ───────────────────────────────────────
function UserPanel({user,color,border,prefs,setPrefs,onClose,setPage}){
  const [showCustomize,setShowCustomize]=useState(false);
  const toggleWidget=id=>{const hidden=prefs.hidden.includes(id)?prefs.hidden.filter(x=>x!==id):[...prefs.hidden,id];const next={...prefs,hidden};setPrefs(next);savePrefs(next);};
  const initials=(user?.name||'AD').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);
  const mi=(icon,label,danger=false,onClick)=>(
    <div key={label} onClick={onClick} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',cursor:'pointer',fontSize:13,color:danger?'#A32D2D':'var(--text-primary)'}} onMouseOver={e=>e.currentTarget.style.background=danger?'#fff1f2':'var(--bg)'} onMouseOut={e=>e.currentTarget.style.background=''}><span style={{color:danger?'#A32D2D':'var(--text-hint)'}}>{icon}</span>{label}</div>
  );
  return(
    <div style={{position:'absolute',top:48,right:0,zIndex:300,background:'#fff',border:`1.5px solid ${border}`,borderRadius:14,width:268,boxShadow:'0 8px 24px rgba(99,102,241,.12)',overflow:'hidden'}}>
      <div style={{display:'flex',gap:10,alignItems:'center',padding:'12px 14px',borderBottom:`1px solid ${border}`,background:`${tint('0a')}`}}>
        <div style={{width:40,height:40,borderRadius:'50%',background:color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700,color:'#fff',flexShrink:0}}>{initials}</div>
        <div style={{minWidth:0}}>
          <div style={{fontSize:14,fontWeight:700,color:'var(--text-primary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user?.name||'Admin'}</div>
          <div style={{fontSize:11,color:'var(--text-hint)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user?.email||''}</div>
          <span style={{fontSize:10,fontWeight:600,background:`${tint('18')}`,color,padding:'2px 8px',borderRadius:10,display:'inline-block',marginTop:3}}>{user?.roleName||'admin'}</span>
        </div>
      </div>
      {mi(IC.user,'My profile',false,()=>{onClose();setPage('settings');})}
      {mi(IC.settings,'Settings',false,()=>{onClose();setPage('settings');})}
      {/* Customize topbar */}
      <div style={{borderBottom:'1px solid var(--border)'}}>
        <div onClick={()=>setShowCustomize(v=>!v)} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 14px',cursor:'pointer',fontSize:13,color,fontWeight:600}} onMouseOver={e=>e.currentTarget.style.background='var(--bg)'} onMouseOut={e=>e.currentTarget.style.background=''}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>{IC.edit} Customize topbar</div>
          <span style={{transform:showCustomize?'rotate(180deg)':'none',transition:'transform .2s',color:'var(--text-hint)'}}>{IC.chevDown}</span>
        </div>
        {showCustomize&&(
          <div style={{padding:'6px 14px 12px',background:'var(--bg)'}}>
            <div style={{fontSize:10,color:'var(--text-hint)',marginBottom:8,display:'flex',alignItems:'center',gap:4}}>{IC.grip} Hold icon 1s to drag · click to toggle</div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--text-hint)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:5}}>System widgets</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5,marginBottom:10}}>
              {ALL_WIDGETS.map(w=>{const on=!prefs.hidden.includes(w.id);return(
                <div key={w.id} onClick={()=>toggleWidget(w.id)} style={{display:'flex',alignItems:'center',gap:7,padding:'6px 9px',borderRadius:8,border:`1px solid ${on?border:'var(--border)'}`,background:on?`${tint('0d')}`:'#fff',cursor:'pointer',fontSize:11,color:on?color:'var(--text-hint)',fontWeight:500,transition:'all .15s'}}>
                  <span style={{flexShrink:0}}>{WIC[w.id]}</span>{w.label}
                  <span style={{marginLeft:'auto',fontSize:9,fontWeight:700,color:on?color:'#A32D2D'}}>{on?'On':'Off'}</span>
                </div>
              );})}
            </div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--text-hint)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:5}}>Quick shortcuts</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5}}>
              {QUICK_ACTIONS.map(qa=>{const on=(prefs.qaVisible||[]).includes(qa.id);return(
                <div key={qa.id} onClick={()=>{const curr=prefs.qaVisible||[];const next=on?curr.filter(x=>x!==qa.id):[...curr,qa.id];const p={...prefs,qaVisible:next};setPrefs(p);savePrefs(p);}} style={{display:'flex',alignItems:'center',gap:7,padding:'6px 9px',borderRadius:8,border:`1px solid ${on?qa.color+'44':'var(--border)'}`,background:on?qa.color+'12':'#fff',cursor:'pointer',fontSize:11,color:on?qa.color:'var(--text-hint)',fontWeight:500,transition:'all .15s'}}>
                  <span style={{flexShrink:0,color:on?qa.color:'var(--text-hint)'}}>{QA_ICONS[qa.id]}</span>{qa.label}
                  <span style={{marginLeft:'auto',fontSize:9,fontWeight:700,color:on?qa.color:'#A32D2D'}}>{on?'On':'Off'}</span>
                </div>
              );})}
            </div>
          </div>
        )}
      </div>
      {mi(IC.logout,'Sign out',true,async()=>{onClose();await supabase.auth.signOut();})}
    </div>
  );
}

// ─── DRAGGABLE WIDGET BUTTON ─────────────────────────────
// Long-press 1.5s activates drag mode — shows grab cursor + pulse ring
function DragWidget({w,menu,color,border,isOpen,onClick,prefs,setPrefs}){
  const [dragReady,setDragReady]=useState(false);
  const longPressTimer=useRef(null);
  const isDragging=useRef(false);

  const startLongPress=()=>{
    longPressTimer.current=setTimeout(()=>{setDragReady(true);},1000);
  };
  const cancelLongPress=()=>{
    clearTimeout(longPressTimer.current);
    if(!isDragging.current) setDragReady(false);
  };
  const onDragStart=e=>{
    isDragging.current=true;
    e.dataTransfer.setData('widgetId',w.id);
    e.dataTransfer.effectAllowed='move';
  };
  const onDragEnd=()=>{
    isDragging.current=false;
    setDragReady(false);
  };
  const onDragOver=e=>e.preventDefault();
  const onDrop=e=>{
    e.preventDefault();
    const srcId=e.dataTransfer.getData('widgetId');
    if(!srcId||srcId===w.id) return;
    const arr=[...prefs.order];
    const from=arr.indexOf(srcId),to=arr.indexOf(w.id);
    if(from<0||to<0) return;
    arr.splice(from,1);arr.splice(to,0,srcId);
    const next={...prefs,order:arr};
    setPrefs(next);savePrefs(next);
  };

  return(
    <button
      draggable={dragReady}
      onMouseDown={startLongPress}
      onMouseUp={cancelLongPress}
      onMouseLeave={cancelLongPress}
      onTouchStart={startLongPress}
      onTouchEnd={cancelLongPress}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onClick}
      title={`${w.label}${dragReady?' — drag to reorder':' · hold 1.5s to drag'}`}
      aria-label={w.label}
      data-menu={menu}
      aria-haspopup="true"
      aria-expanded={isOpen}
      style={{
        width:34,height:34,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',
        border:`1px solid ${isOpen?color:border}`,
        cursor:dragReady?'grab':'pointer',
        color:`${tint('cc')}`,
        background:isOpen?`${tint('18')}`:'rgba(255,255,255,0.75)',
        position:'relative',transition:'all .15s',flexShrink:0,
        outline:dragReady?`2px solid ${color}`:'none',
        animation:dragReady?'tbPulse 0.6s infinite':'none',
      }}
    >
      {WIC[w.id]}
      {w.dot&&<div style={{position:'absolute',top:5,right:5,width:7,height:7,borderRadius:'50%',background:w.dot,border:'1.5px solid rgba(255,255,255,.9)'}}/>}
      {dragReady&&<div style={{position:'absolute',inset:-3,borderRadius:10,border:`2px solid ${color}`,opacity:.5,pointerEvents:'none'}}/>}
    </button>
  );
}

// ─── SUPERADMIN CAMPUS SWITCHER ──────────────────────────
// Visible only to platform admins. my_campuses() returns every school for them. Switching goes through
// the one shared path (switch_campus() in the database, then a page reload), so nothing is half-switched.
function SchoolSwitcher({ color, border, toast, open, onToggle, onClose, campuses, active }){
  const user = useStore(s => s.user);
  const isPlatformAdmin = !!user?.isPlatformAdmin;
  const [switching, setSwitching] = useState(false);

  if (!isPlatformAdmin) return null;

  const currentName = active?.name || 'Unknown school';

  const handleSwitch = async (c) => {
    if (c.is_active || switching) return;
    setSwitching(true);
    try {
      await switchAndReload(c.school_id, c.name);
    } catch (err) {
      toast.error(err.message || 'Could not switch campus.');
      setSwitching(false);
    }
  };

  return (
    <div data-menu="school" style={{ position: 'relative' }}>
      <button
        onClick={onToggle}
        aria-haspopup="true"
        aria-expanded={open}
        disabled={switching}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', border: `1.5px solid #F59E0B`, borderRadius: 20, cursor: switching ? 'not-allowed' : 'pointer', background: '#FFFBEB', color: '#92400E', fontSize: 12, fontWeight: 700, opacity: switching ? 0.6 : 1, flexShrink: 0 }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#F59E0B', flexShrink: 0 }} />
        {switching ? 'Switching…' : `Superadmin · ${currentName}`}
        {IC.chevDown}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 38, left: 0, zIndex: 300, background: '#fff', border: `1.5px solid ${border}`, borderRadius: 12, width: 280, maxHeight: 340, overflowY: 'auto', boxShadow: '0 8px 24px rgba(99,102,241,.15)' }}>
          <div style={{ padding: '9px 14px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '.4px' }}>
            Switch campus
          </div>
          {campuses.length === 0 ? (
            <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-hint)' }}>No campuses found.</div>
          ) : campuses.map(c => {
            const isCurrent = !!c.is_active;
            return (
              <div
                key={c.school_id}
                onClick={() => handleSwitch(c)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 14px', cursor: isCurrent || switching ? 'default' : 'pointer', background: isCurrent ? `${tint('12')}` : 'transparent' }}
                onMouseOver={e => { if (!isCurrent) e.currentTarget.style.background = 'var(--bg)'; }}
                onMouseOut={e => { e.currentTarget.style.background = isCurrent ? `${tint('12')}` : 'transparent'; }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: isCurrent ? 700 : 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                  {c.organization_name && <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-hint)' }}>{c.organization_name}</span>}
                </span>
                {isCurrent && <span style={{ fontSize: 10, fontWeight: 700, color, flexShrink: 0 }}>Current</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── MAIN TOPBAR ─────────────────────────────────────────
// Which dropdown each widget button opens
const MENU_OF = {events:'calendar',notifications:'notif',help:'help'};

export default function Topbar(){
  const {page,setPage,user,hasPermission,settings}=useStore();
  const products=useStore(s=>s.products);
  const productStock=useStore(s=>s.productStock);
  // null | 'search' | 'calendar' | 'notif' | 'help' | 'profile' | 'school'
  const [openMenu,setOpenMenu]=useState(null);
  const [prefs,setPrefs]=useState(loadPrefs);
  const { toasts, removeToast, toast } = useToast();
  const { campuses, active: activeCampus, multi } = useCampuses();

  // After a campus switch the page reloads; say where we landed (once).
  useEffect(()=>{ const n=takeSwitchedCampusName(); if(n) toast.success(`Switched to ${n}`); },[]); // eslint-disable-line

  // ── Upcoming events: school_events + holidays, next 30 days, merged and sorted ──
  const [events,setEvents]=useState([]);
  const [eventsLoading,setEventsLoading]=useState(true);
  useEffect(()=>{
    if(!user?.schoolId) return undefined;
    let alive=true;
    setEventsLoading(true);
    const todayStr=todayISO();
    const in30=new Date(); in30.setDate(in30.getDate()+30);
    const in30Str=localISO(in30);
    Promise.all([db.getSchoolEvents(user.schoolId),db.getHolidaysInRange(user.schoolId,todayStr,in30Str)])
      .then(([schoolEvents,holidays])=>{
        if(!alive) return;
        const merged=[
          ...(schoolEvents||[]).filter(e=>e.start_date>=todayStr&&e.start_date<=in30Str)
            .map(e=>({id:`ev-${e.id}`,date:e.start_date,title:e.title,subtitle:e.description||'',category:e.category||'general'})),
          ...(holidays||[]).map(h=>({id:`ho-${h.id}`,date:h.start_date,title:h.title,subtitle:'School closed',category:'holiday'})),
        ].sort((a,b)=>a.date.localeCompare(b.date));
        setEvents(merged); setEventsLoading(false);
      })
      .catch(()=>{ if(alive) setEventsLoading(false); });
    return ()=>{ alive=false; };
  },[user?.schoolId]);

  // ── Notifications: a handful of real, computed signals, each gated by the same page
  // permission that already guards that module's own page — not a generic events log (none
  // exists yet), just "things this user's role would want to know about right now". ──
  const [notifItems,setNotifItems]=useState([]);
  const [notifLoading,setNotifLoading]=useState(true);
  useEffect(()=>{
    if(!user?.schoolId) return undefined;
    let alive=true;
    setNotifLoading(true);
    const schoolId=user.schoolId;
    const curSym=settings?.currencySymbol||'';
    (async()=>{
      const out=[];
      const now=new Date();
      const todayStr=localISO(now);

      // Unpaid fees this month (fees.view)
      if(hasPermission('fees.view')){
        try{
          const { data } = await supabase.from('challans')
            .select('family_id,net_payable,paid_amount')
            .eq('school_id',schoolId).eq('month',now.getMonth()+1).eq('year',now.getFullYear())
            .neq('status','draft');
          const unpaid=(data||[]).filter(c=>Number(c.net_payable||0)>Number(c.paid_amount||0));
          if(unpaid.length){
            const families=new Set(unpaid.map(c=>c.family_id)).size;
            const due=unpaid.reduce((s,c)=>s+(Number(c.net_payable||0)-Number(c.paid_amount||0)),0);
            out.push({bg:'#FCEBEB',c:'#A32D2D',dc:'#E24B4A',
              text:`${families} famil${families===1?'y has':'ies have'} unpaid fees`,
              sub:`This month · ${curSym} ${Math.round(due).toLocaleString('en-US')} due`,
              onClick:()=>{setPage('fees');setOpenMenu(null);}});
          }
        }catch{/* permission or table shape mismatch — skip this signal, don't break the rest */}
      }

      // Low stock — reuses the exact same numbers already shown on the Inventory page, no extra query
      if(hasPermission('inventory.view')){
        const low=(products||[]).filter(p=>(productStock[p.id]||0)<=p.minStock&&p.minStock>0);
        if(low.length){
          out.push({bg:'#FAEEDA',c:'#854F0B',dc:'#EF9F27',
            text:`${low.length} item${low.length===1?'':'s'} below minimum stock`,
            sub:low.slice(0,3).map(p=>p.name).join(', ')+(low.length>3?`, +${low.length-3} more`:''),
            onClick:()=>{setPage('inventory');setOpenMenu(null);}});
        }
      }

      // New admissions today (students.view)
      if(hasPermission('students.view')){
        try{
          const { data } = await supabase.from('students').select('name')
            .eq('school_id',schoolId).eq('admission_date',todayStr).limit(10);
          if(data&&data.length){
            out.push({bg:'#EAF3DE',c:'#3B6D11',dc:'#E24B4A',
              text:`${data.length} new admission${data.length===1?'':'s'} today`,
              sub:data.map(s=>s.name).join(' · '),
              onClick:()=>{setPage('students');setOpenMenu(null);}});
          }
        }catch{/* skip */}
      }

      // Exam marks entered but not yet finalised/approved (exams.view)
      if(hasPermission('exams.view')){
        try{
          const { count } = await supabase.from('exams').select('id',{count:'exact',head:true})
            .eq('school_id',schoolId).eq('marks_status','entered');
          if(count){
            out.push({bg:'#EEEDFE',c:'#3C3489',dc:null,
              text:`${count} exam${count===1?'':'s'} with marks pending approval`,
              sub:'Entered, not yet finalised',
              onClick:()=>{setPage('exams');setOpenMenu(null);}});
          }
        }catch{/* skip */}
      }

      if(alive){ setNotifItems(out); setNotifLoading(false); }
    })();
    return ()=>{ alive=false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[user?.schoolId]);

  const gradient=THEME_GRADIENT;
  const border=THEME_BORDER;
  const color=THEME_COLOR;

  const firstName=(user?.name||'Admin').split(' ')[0];
  const initials=(user?.name||'AD').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);

  // Opening one menu closes whichever other one was open; clicking its own trigger again closes it.
  const toggleMenu=name=>setOpenMenu(m=>m===name?null:name);
  const closeMenu=()=>setOpenMenu(null);

  // One outside-click / Escape handler for every dropdown. A trigger and its panel both carry
  // data-menu="<name>", so a press on either is "inside" and the trigger's own click toggles the
  // menu. (Closing on mousedown for the trigger itself is what made the second click reopen it.)
  useEffect(()=>{
    if(!openMenu) return undefined;
    const onDown=e=>{
      const t=e.target;
      if(t&&t.closest&&t.closest(`[data-menu="${openMenu}"]`)) return;
      setOpenMenu(null);
    };
    const onKey=e=>{if(e.key==='Escape') setOpenMenu(null);};
    document.addEventListener('mousedown',onDown);
    document.addEventListener('keydown',onKey);
    return()=>{document.removeEventListener('mousedown',onDown);document.removeEventListener('keydown',onKey);};
  },[openMenu]);

  const visible=prefs.order.map(id=>ALL_WIDGETS.find(w=>w.id===id)).filter(w=>w&&!prefs.hidden.includes(w.id));

  const next=events[0]||null;
  const nd=next?new Date(next.date):null;
  const ndStr=nd?formatDayMonth(next.date):'';
  const eventsWidgetVisible=visible.some(w=>w.id==='events');

  const faqs=faqsFor(user,hasPermission);

  return(
    <>
      <header style={{background:gradient,borderBottom:`1px solid ${border}`,padding:'0 18px',height:'var(--topbar-h)',display:'flex',alignItems:'center',gap:8,flexShrink:0,position:'relative',zIndex:100}}>

        {/* Left */}
        <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
          <span style={{fontSize:15,fontWeight:700,color:'var(--text-primary)',flexShrink:0}}>{PAGE_TITLES[page]||'EdEase'}</span>
          <span style={{fontSize:11,color:`${tint('99')}`,flexShrink:0}}>{PAGE_SUBS[page]||''}</span>
          {next&&(
            <div style={{position:'relative'}}>
              <button type="button" data-menu="calendar" aria-haspopup="true" aria-expanded={openMenu==='calendar'} onClick={()=>toggleMenu('calendar')} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 11px',background:'rgba(255,255,255,0.75)',border:`1px solid ${border}`,borderRadius:20,cursor:'pointer',minWidth:0,maxWidth:310,flexShrink:1,font:'inherit',textAlign:'left'}}>
                <div style={{width:6,height:6,borderRadius:'50%',background:color,flexShrink:0,animation:'tbPulse 2s infinite'}}/>
                <span style={{fontSize:11,fontWeight:700,color,flexShrink:0}}>{ndStr}</span>
                <span style={{fontSize:11,color:'#374151',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontWeight:500}}>{next.title}</span>
                {next.subtitle&&<span style={{fontSize:10,color:`${tint('88')}`,flexShrink:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:110}}>{next.subtitle}</span>}
              </button>
              {openMenu==='calendar'&&!eventsWidgetVisible&&<EventsPanel border={border} events={events} loading={eventsLoading}/>}
            </div>
          )}
        </div>

        {/* Right */}
        <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>

          <GlobalSearch open={openMenu==='search'} onOpenChange={v=>setOpenMenu(m=>v?'search':(m==='search'?null:m))} />

          {/* Quick-action shortcut buttons */}
          {(prefs.qaVisible||[]).length>0&&(
            <div style={{display:'flex',alignItems:'center',gap:3}}>
              {QUICK_ACTIONS.filter(qa=>(prefs.qaVisible||[]).includes(qa.id)).map(qa=>(
                <button key={qa.id} onClick={()=>setPage(qa.page)} title={`Go to ${qa.label}`} aria-label={qa.label} style={{width:34,height:34,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',border:`1px solid ${border}`,cursor:'pointer',color:qa.color,background:'rgba(255,255,255,0.75)',flexShrink:0,transition:'all .15s'}} onMouseOver={e=>{e.currentTarget.style.background=qa.color+'18';e.currentTarget.style.borderColor=qa.color;}} onMouseOut={e=>{e.currentTarget.style.background='rgba(255,255,255,0.75)';e.currentTarget.style.borderColor=border;}}>
                  {QA_ICONS[qa.id]}
                </button>
              ))}
            </div>
          )}

          {/* Calendar / notifications / help buttons (drag to reorder) — each wrapped so its
              dropdown is positioned relative to its OWN button, not the whole button row */}
          <div style={{display:'flex',alignItems:'center',gap:3,padding:'3px 5px'}}>
            {visible.map(w=>(
              <div key={w.id} style={{position:'relative'}}>
                <DragWidget w={w} menu={MENU_OF[w.id]} color={color} border={border} isOpen={openMenu===MENU_OF[w.id]} onClick={()=>toggleMenu(MENU_OF[w.id])} prefs={prefs} setPrefs={setPrefs}/>
                {openMenu===MENU_OF[w.id]&&w.id==='events'&&<EventsPanel border={border} events={events} loading={eventsLoading}/>}
                {openMenu===MENU_OF[w.id]&&w.id==='notifications'&&<NotifPanel border={border} items={notifItems} loading={notifLoading}/>}
                {openMenu===MENU_OF[w.id]&&w.id==='help'&&<HelpPanel border={border} faqs={faqs} onClose={closeMenu}/>}
              </div>
            ))}
          </div>

          {/* Superadmin school switcher — only renders for platform admins */}
          {/* Which campus am I in? Super admins see it inside the pill below; everyone else with 2+ campuses gets this chip. */}
          {!user?.isPlatformAdmin&&multi&&activeCampus&&(
            <span title="Active campus" style={{display:'inline-flex',alignItems:'center',gap:6,height:34,padding:'0 12px',borderRadius:20,border:`1px solid ${border}`,background:'rgba(255,255,255,0.75)',fontSize:12,fontWeight:600,color:'var(--text-primary)',flexShrink:0,maxWidth:200}}>
              <Building2 size={14} aria-hidden="true" style={{color:'var(--primary)',flex:'none'}}/>
              <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{activeCampus.name}</span>
            </span>
          )}

          {/* Superadmin campus switcher — only renders for platform admins */}
          <SchoolSwitcher color={color} border={border} toast={toast} open={openMenu==='school'} onToggle={()=>toggleMenu('school')} onClose={closeMenu} campuses={campuses} active={activeCampus} />

          {/* User pill */}
          <div style={{position:'relative'}} data-menu="profile">
            <button type="button" aria-haspopup="true" aria-expanded={openMenu==='profile'} onClick={()=>toggleMenu('profile')} style={{display:'flex',alignItems:'center',gap:7,padding:'4px 10px 4px 4px',border:`1px solid ${openMenu==='profile'?color:border}`,borderRadius:20,cursor:'pointer',background:openMenu==='profile'?`${tint('18')}`:'rgba(255,255,255,0.75)',transition:'all .15s'}}>
              <div style={{width:28,height:28,borderRadius:'50%',background:color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:'#fff'}}>{initials}</div>
              <span style={{fontSize:13,fontWeight:600,color:'var(--text-primary)'}}>{firstName}</span>
              {IC.chevDown}
            </button>
            {openMenu==='profile'&&<UserPanel user={user} color={color} border={border} prefs={prefs} setPrefs={setPrefs} onClose={closeMenu} setPage={setPage}/>}
          </div>

          {/* Floating panels */}
        </div>
      </header>

      <style>{`@keyframes tbPulse{0%,100%{opacity:1}50%{opacity:.3}}`}{TOAST_ANIM}</style>
      <Toast toasts={toasts} removeToast={removeToast} />
    </>
  );
}
